import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Grid from '@material-ui/core/Grid';

import Tooltip from 'components/common/Tooltip';
import Typography from '@material-ui/core/Typography';
import IconSMS from 'components/icons/IconSMS';
import IconEmail from 'components/icons/IconEmail';
import IconRural from 'components/icons/IconRural';
import { SectionBodyEmptyContent } from 'components/common/Section';
import IconNotepad from 'components/icons/IconNotepad';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';

import getPatientFullAddress from 'utils/print/getPatientFullAddress';
import objectDiff from 'utils/objectDiff';
import primaryLanguages from 'config/primaryLanguages';

const styles = theme => ({
  table: {
    width: 'auto',
  },
  tableRow: {
    height: 24,
  },
  tableCell: {
    border: 'none',
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5),
    padding: '0 16px 4px 0',
    verticalAlign: 'top',
    [theme.breakpoints.down('sm')]: {
      '&:last-child': {
        paddingBottom: 4,
      },
    },
  },
  tableCellCaption: {
    width: 'auto',
    color: theme.palette.grey[700],
    [theme.breakpoints.down('sm')]: {
      width: 112,
    },
  },
  iconSMS: {
    marginRight: 8,
    marginTop: -2,
    color: theme.palette.primary[500],
  },
  iconEmail: {
    marginRight: 8,
    marginTop: -4,
    color: theme.palette.primary[500],
  },
  tooltip: {
    verticalAlign: 'top',
  },
  iconNotepad: {
    width: 18,
    height: 22,
  },
  iconRural: {
    width: 24,
    height: 24,
    marginRight: 8,
    marginTop: -6,
    color: theme.palette.primary[500],
  },
  tooltipLanguage: {
    marginRight: 4,
    fontSize: pxToRem(16),
    verticalAlign: 'bottom',
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'inline-block',
    },
  },
  iconPrimaryLanguage: {
    width: 16,
    height: 16,
    verticalAlign: 'top',
  },
});

const ContactInformationView = props => {
  const deliverySameAsBilling = Object.keys(objectDiff(props['Address Information'],
    props['Billing Address Information'])).length === 0;
  const IconPrimaryLanguage = props['Primary Language']
    ? primaryLanguages.find(item => item.code === props['Primary Language']).icon
    : null;
  return (Object.keys(props['Contact Information']).length > 0
    || Object.keys(props['Address Information']).length > 0
    || Object.keys(props['Billing Address Information']).length > 0) ? (
    <Fragment>
      <Grid
        container
        justify="space-between"
        spacing={0}
        wrap="wrap"
      >
        <Grid item xs={12} md={5}>
          <Table className={props.classes.table}>
            <TableBody>
              <TableRow className={props.classes.tableRow}>
                <TableCell
                  padding="none"
                  className={classNames(props.classes.tableCell, props.classes.tableCellCaption)}
                >
                  Cell Phone
                </TableCell>
                <TableCell className={props.classes.tableCell}>
                  {props['Primary Language'] &&
                    <Tooltip
                      className={props.classes.tooltipLanguage}
                      content={
                        <Typography>
                          Primary Language: {primaryLanguages.find(item => item.code === props['Primary Language']).name}
                        </Typography>
                      }
                      placement="top"
                    >
                      <IconPrimaryLanguage className={props.classes.iconPrimaryLanguage} />
                    </Tooltip>
                  }
                  {props['Contact Information']['Primary Phone']}
                </TableCell>
              </TableRow>
              {props['Contact Information']['Home Phone'] &&
                <TableRow className={props.classes.tableRow}>
                  {props['Contact Information']['Home Phone'] &&
                    <Fragment>
                      <TableCell
                        padding="none"
                        className={classNames(props.classes.tableCell, props.classes.tableCellCaption)}
                      >
                        Home Phone
                      </TableCell>
                      <TableCell className={props.classes.tableCell}>
                        {props['Contact Information']['Home Phone']}
                      </TableCell>
                    </Fragment>
                  }
                </TableRow>
              }
              {props['Contact Information']['Work Phone'] &&
                <TableRow className={props.classes.tableRow}>
                  <Fragment>
                    <TableCell
                      padding="none"
                      className={classNames(props.classes.tableCell, props.classes.tableCellCaption)}
                    >
                      Emergency
                    </TableCell>
                    <TableCell className={props.classes.tableCell}>
                      {props['Contact Information']['Work Phone']}
                      {props['Contact Information']['Name Of Emergency Contact'] && ' - '}
                      {props['Contact Information']['Name Of Emergency Contact'] &&
                        <div>
                          {props['Contact Information']['Name Of Emergency Contact']}
                        </div>
                      }
                    </TableCell>
                  </Fragment>
                </TableRow>
              }
              {props['Contact Information'].Email &&
                <TableRow className={props.classes.tableRow}>
                  <TableCell
                    padding="none"
                    className={classNames(props.classes.tableCell, props.classes.tableCellCaption)}
                  >
                    {props['Contact Information'].Email && 
                      'Email'
                    }
                  </TableCell>
                  <TableCell className={props.classes.tableCell}>
                    {props['Contact Information'].Email}
                  </TableCell>
                </TableRow>
              }
            </TableBody>
          </Table>
        </Grid>


        <Grid item xs={12} md={7}>
          <Table className={props.classes.table}>
            <TableBody>
              <TableRow className={props.classes.tableRow}>
                <TableCell
                  padding="none"
                  className={classNames(props.classes.tableCell, props.classes.tableCellCaption)}
                >
                  {deliverySameAsBilling ? 'Billing/Delivery Address' : 'Billing Address'}
                </TableCell>
                <TableCell padding="none" className={props.classes.tableCell}>
                  {props['Billing Address Information']['Is Rural'] &&
                    <Tooltip
                      className={props.classes.tooltip}
                      content={
                        <Typography>
                          {deliverySameAsBilling ? 'Rural Billing/Delivery Address' : 'Rural Billing Address'}
                        </Typography>
                      }
                      placement="top"
                    >
                      <IconRural className={props.classes.iconRural} />
                    </Tooltip>
                  }
                  {getPatientFullAddress({ patient: props, billing: true })}
                </TableCell>
              </TableRow>
              {!deliverySameAsBilling &&
                <TableRow className={props.classes.tableRow}>
                  <TableCell
                    padding="none"
                    className={classNames(props.classes.tableCell, props.classes.tableCellCaption)}
                  >
                    Delivery Address
                  </TableCell>
                  <TableCell padding="none" className={props.classes.tableCell}>
                    {props['Address Information']['Is Rural'] &&
                      <Tooltip
                        className={props.classes.tooltip}
                        content={
                          <Typography>
                            Rural Delivery Address
                          </Typography>
                        }
                        placement="top"
                      >
                        <IconRural className={props.classes.iconRural} />
                      </Tooltip>
                    }
                    {getPatientFullAddress({ patient: props })}
                  </TableCell>
                </TableRow>
              }
              {props['Contact Information']['Preferred method of communications'] !== 'None' &&
                <TableRow className={props.classes.tableRow}>
                  <TableCell
                    padding="none"
                    className={classNames(props.classes.tableCell, props.classes.tableCellCaption)}
                  >
                    Notifications by:
                  </TableCell>
                  <TableCell padding="none" className={props.classes.tableCell}>
                    {(props['Contact Information']['Preferred method of communications'] === 'Both'
                      || props['Contact Information']['Preferred method of communications'] === 'By SMS') && (
                      <Tooltip
                        className={props.classes.tooltip}
                        content={
                          <Typography>
                            SMS
                          </Typography>
                        }
                        placement="top"
                      >
                        <IconSMS className={props.classes.iconSMS} />
                      </Tooltip>
                    )}
                          
                    {(props['Contact Information']['Preferred method of communications'] === 'Both'
                      || props['Contact Information']['Preferred method of communications'] === 'By Email') && (
                        <Tooltip
                          className={props.classes.tooltip}
                          content={
                            <Typography>
                              Email
                            </Typography>
                          }
                          placement="top"
                        >
                          <IconEmail className={props.classes.iconEmail} />
                        </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              }
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </Fragment>
  ) : (
    <SectionBodyEmptyContent
      text="There is no Contact Information"
      icon={(
        <IconNotepad className={props.classes.iconNotepad} />
      )}
      disablePadding
    />
  );
};

ContactInformationView.propTypes = {
  'Contact Information': PropTypes.shape({
    'Primary Phone': PropTypes.string,
    'Home Phone': PropTypes.string,
    'Work Phone': PropTypes.string,
    'Preferred method of communications': PropTypes
      .oneOf(['By Email', 'By SMS', 'Both', 'None']),
    Email: PropTypes.string,
  }).isRequired,
  classes: PropTypes.shape({
    table: PropTypes.string.isRequired,
    tableRow: PropTypes.string.isRequired,
    tableCell: PropTypes.string.isRequired,
    tableCellCaption: PropTypes.string.isRequired,
  }).isRequired,
};

export default withStyles(styles)(ContactInformationView);



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/ContactInformation/ContactInformationView.js